import { useState, type FormEvent } from 'react';
import { SESSION_EXPIRED_FLAG, useAuth } from './AuthProvider';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { getPrefill, clearPrefill, takeAuthNotice, type AuthNotice } from '../lib/api';

/** V219 — textos del aviso (los mismos que manda el backend en el 401). */
export const AUTH_NOTICE_TEXT: Record<AuthNotice, string> = {
  access_changed: 'Tu acceso cambió, vuelve a iniciar sesión.',
  protected: 'Soporte de FluidSol protegió tu cuenta. Para volver a entrar usa "¿Olvidaste tu contraseña?": te llegará un correo para crear una nueva.',
};

export function LoginScreen() {
  const { login, loading } = useAuth();
  // Carril K — pre-fill: recordar tenant + correo (NO la contraseña) al volver.
  const prefill = getPrefill();
  // §1bis — la liga del correo de confirmación trae ?negocio=<identificador>.
  const negocioParam = new URLSearchParams(window.location.search).get('negocio')?.trim().toLowerCase() || '';
  const [tenantSlug, setTenantSlug] = useState(negocioParam || prefill?.tenantSlug || '');
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [password, setPassword] = useState('');
  // "Recordar sesión": opt-in, DEFAULT APAGADO (default seguro).
  const [remember, setRemember] = useState(false);
  const [prefilled, setPrefilled] = useState(!!prefill);
  const [error, setError] = useState<string | null>(null);
  // feat/turnstile — token anti-bot, best-effort: si el widget resuelve, el
  // token viaja con el submit; si no (sin site key, script bloqueado, widget
  // caído), el submit va SIN token y el BACKEND decide (fully fail-open en el
  // frontend — fix: el gating del botón dejaba el login bloqueado si el
  // widget no llegaba a resolver).
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Fase 0 — aviso cuando se llegó aquí por timeout de inactividad. Se lee una
  // sola vez (lazy init) y se limpia el flag para que no reaparezca al recargar.
  const [sessionExpired] = useState(() => {
    const flag = sessionStorage.getItem(SESSION_EXPIRED_FLAG) === '1';
    if (flag) sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    return flag;
  });
  // V219 — la sesión se cerró porque cambió su acceso o soporte protegió la cuenta.
  const [authNotice] = useState(takeAuthNotice);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password, tenantSlug, { remember, turnstileToken: turnstileToken ?? undefined });
    } catch (err: any) {
      setError(err?.message ?? 'Credenciales inválidas');
    }
  };

  // "Usar otra cuenta": limpia el pre-fill y los campos.
  const useAnotherAccount = () => {
    clearPrefill();
    setPrefilled(false);
    setTenantSlug('');
    setEmail('');
    setPassword('');
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAF7] px-4">
      <div className="card-soft w-full max-w-sm rounded-2xl border border-slate-200/80 bg-white p-8">
        <div className="mb-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
            FluidSol
          </p>
          <h1 className="font-serif text-2xl tracking-tight text-slate-900">
            Iniciar sesión
          </h1>
        </div>

        {authNotice && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {AUTH_NOTICE_TEXT[authNotice]}
          </div>
        )}
        {sessionExpired && !authNotice && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Tu sesión expiró por inactividad. Inicia sesión de nuevo para continuar.
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Identificador del negocio
            </span>
            <input
              className="input"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              autoComplete="organization"
              required
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Email
            </span>
            <input
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Password
            </span>
            <input
              className="input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          {/* Carril K — "Recordar sesión": opt-in, default apagado. */}
          <div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>Recordar sesión</span>
            </label>
            <p className="ml-6 mt-0.5 text-[11px] text-slate-400">
              No lo marques en equipos compartidos.
            </p>
          </div>

          {prefilled && (
            <button
              type="button"
              onClick={useAnotherAccount}
              className="block text-left text-xs font-medium text-slate-500 underline hover:text-slate-800"
            >
              Usar otra cuenta
            </button>
          )}

          {error && (
            <p className="alert-over rounded-xl px-3 py-2 text-xs">{error}</p>
          )}

          <TurnstileWidget onToken={setTurnstileToken} />

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-50"
          >
            {loading ? 'Entrando…' : 'Entrar'}
          </button>

          <a
            href="/auth/forgot-password"
            className="block text-center text-xs font-medium text-slate-500 underline hover:text-slate-800"
          >
            ¿Olvidaste tu contraseña?
          </a>
        </form>
      </div>
    </div>
  );
}
